import React, { ReactNode } from "react";

import { Link, SpaceBetween } from "@amzn/awsui-components-console";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { useAppSelector } from "src/redux/hooks";
import type { RootState } from "src/redux/store";
import { getWcCountryConfigsForRoles, WcCountryConfig } from "src/app/wcCountryConfig";

export const INVALID_ASSOCIATE_HOME_SITE_ERROR = "invalidAssociateHomeSiteError";

/**
 * Renders the Canada-specific error content with SIM and RIVER links.
 * Accepts an optional preamble to customize the introductory text
 * (e.g. for site-not-found vs claim submission errors).
 * Defaults to the site-not-found preamble when none is provided.
 */
export const CanadaErrorContent = ({
  config,
  preamble,
}: {
  config: WcCountryConfig;
  preamble?: ReactNode;
}) => (
  <div>
    {preamble ?? <div>Please perform BOTH of the following steps.</div>}
    <ol>
      <li>
        <Link href={config.missingSiteTicketUrl} target="_blank" external>
          Click here to submit a SIM
        </Link>{" "}
        to report this issue to the Nucleus Tech team and have your site entered into Nucleus
      </li>
      <li>
        Go to{" "}
        <Link href={config.wcClaimReferralUrl} target="_blank" external>
          RIVER
        </Link>{" "}
        and submit the WC Claim referral manually to prevent a late reporting to the WC board.
      </li>
    </ol>
  </div>
);

/**
 * Renders the default US "site not found" error content.
 */
const UsCountryErrorContent = () => (
  <div>
    Nucleus is only launched to Amcare supported sites with GCF, Air &amp; Sort business lines. If
    you&apos;re part of this group, please{" "}
    <SimLinkGenerator target="_blank" simType={SIM_TYPES.REPORT_MISSING_SITE}>
      create a ticket
    </SimLinkGenerator>{" "}
    for us to investigate. All other business lines should use their existing process as defined in
    the{" "}
    <Link
      href="https://w.amazon.com/bin/view/Main/Risk_Management/US_Workers_Compensation/ReportingClaim"
      target="_blank"
      external
    >
      Workers&apos; Compensation Claims Wiki.
    </Link>
  </div>
);

/**
 * Renders the error content for a given country config.
 * Non-US countries with wcClaimReferralUrl get the referral flow;
 * US (or configs without referral URLs) get the default US content.
 */
const CountryErrorContent = ({ config }: { config: WcCountryConfig }) =>
  config.wcClaimReferralUrl && config.missingSiteTicketUrl ? (
    <CanadaErrorContent config={config} />
  ) : (
    <UsCountryErrorContent />
  );

const InvalidAssociateHomeSiteError = () => {
  const { user } = useAppSelector((state: RootState) => state.auth);
  const userRoles = user?.roles ?? [];
  const matchingConfigs = getWcCountryConfigsForRoles(userRoles);
  const showCountryLabels = matchingConfigs.length >= 2;

  return (
    <SpaceBetween direction="vertical" size="xs">
      <div>The system was unable to identify the site for this employee.</div>
      {matchingConfigs.map((config) => (
        <div key={config.countryCode}>
          {showCountryLabels && (
            <div>
              <strong>For Associates in {config.label}:</strong>
            </div>
          )}
          <CountryErrorContent config={config} />
        </div>
      ))}
    </SpaceBetween>
  );
};

export interface CustomErrorConfig {
  header: string;
  content: React.ReactNode;
}

export const CUSTOM_ERRORS: Record<string, CustomErrorConfig> = {
  [INVALID_ASSOCIATE_HOME_SITE_ERROR]: {
    header: "Site not found",
    content: <InvalidAssociateHomeSiteError />,
  },
};
